import { useEffect, useState } from "react";
import { Plus, Pencil, Link2, RefreshCw, CheckSquare } from "lucide-react";
import api from "@/lib/api";
import JobsCostCodesTabs from "@/components/JobsCostCodesTabs";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CsvImportButton from "@/components/CsvImportButton";
import SearchableSelect from "@/components/SearchableSelect";

const emptyForm = { code: "", name: "", category: "" };

// Package 28 — account types the Select Cost Codes screen pre-unchecks
// (banks/loans/payroll aren't job-cost buckets); the server also flags
// this per-account as default_excluded, kept here only as a fallback.
const DEFAULT_EXCLUDED_TYPES = new Set(["Bank", "Credit Card", "Other Current Liability", "Long Term Liability", "Equity"]);

export default function CostCodes() {
  const [codes, setCodes] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [governed, setGoverned] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [selectable, setSelectable] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [savingSelection, setSavingSelection] = useState(false);
  // Test Drive 2026-10-21 — every QuickBooks account (sub-accounts too),
  // searchable and filterable by account type.
  const [selectSearch, setSelectSearch] = useState("");
  const [selectType, setSelectType] = useState("");

  const load = () => api.get("/contractor/cost-codes", { params: { include_inactive: true } }).then((r) => setCodes(r.data));
  const loadGovernance = () => api.get("/contractor/integrations/qbo/status").then((r) => setGoverned(r.data.governed)).catch(() => {});
  useEffect(() => { load(); loadGovernance(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (cc) => { setEditing(cc); setForm({ code: cc.code, name: cc.name, category: cc.category || "" }); setShow(true); };

  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code and name required");
    try {
      if (editing) {
        await api.patch(`/contractor/cost-codes/${editing.id}`, form);
        toast.success("Cost code updated");
      } else {
        await api.post("/contractor/cost-codes", form);
        toast.success("Cost code created");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save cost code");
    }
  };

  const toggleActive = async (cc) => {
    try {
      await api.patch(`/contractor/cost-codes/${cc.id}/active`, { active: !cc.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post("/contractor/integrations/qbo/sync-inbound");
      toast.success("Synced with QuickBooks");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openSelect = async () => {
    const { data } = await api.get("/contractor/integrations/qbo/select/cost-codes");
    setSelectable(data);
    setCheckedIds(data.filter((r) => r.selected).map((r) => r.qbo_id));
    setShowSelect(true);
  };

  const toggleChecked = (qboId) => setCheckedIds((ids) => ids.includes(qboId) ? ids.filter((i) => i !== qboId) : [...ids, qboId]);

  const saveSelection = async () => {
    setSavingSelection(true);
    try {
      const { data } = await api.post("/contractor/integrations/qbo/select/cost-codes", { qbo_ids: checkedIds });
      toast.success(`${data.selected} cost code(s) selected — ${data.created} newly added, ${data.deactivated} deactivated`);
      setShowSelect(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save selection");
    } finally {
      setSavingSelection(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="cost-codes-page">
      <JobsCostCodesTabs />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Cost Codes</h1>
          <p className="text-slate-500 text-sm mt-1">Required on every purchase request line item.</p>
        </div>
        <div className="flex items-center gap-2">
          {governed ? (
            <button onClick={openSelect} data-testid="select-qbo-cost-codes-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
              <CheckSquare size={14} /> Select Accounts
            </button>
          ) : (
            <>
              <CsvImportButton entity="cost-codes" label="Cost Codes" onImported={load} />
              <button onClick={openNew} data-testid="new-cost-code-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
                <Plus size={14} /> New Cost Code
              </button>
            </>
          )}
        </div>
      </div>

      {governed && (
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap text-[13px] bg-sky-50 border border-sky-100 rounded-lg px-3.5 py-3" data-testid="qbo-governed-banner">
          <div className="flex items-center gap-2 text-sky-900">
            <Link2 size={15} /> Managed by QuickBooks — cost codes are mirrored from your chart of accounts and selected above, not created here.
          </div>
          <button onClick={syncNow} disabled={syncing} data-testid="qbo-sync-now-button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-200 text-[12px] font-semibold hover:bg-sky-100 disabled:opacity-60 shrink-0">
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="cost-codes-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Code</th><th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Category</th><th className="text-left py-2.5">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((cc) => (
              <tr key={cc.id} className={`border-b border-slate-50 ${!cc.active ? "opacity-50" : ""}`} data-testid={`cost-code-row-${cc.id}`}>
                <td className="py-3 font-mono font-medium">{cc.code}</td>
                <td className="py-3">{cc.name}{cc.source === "quickbooks" && <span className="ml-2 text-[10.5px] font-semibold text-sky-600 align-middle">QB</span>}</td>
                <td className="py-3 text-slate-500">{cc.category || "—"}</td>
                <td className="py-3">{cc.active ? "Active" : "Inactive"}</td>
                <td className="py-3 text-right space-x-3">
                  {!governed && (
                    <>
                      <button onClick={() => openEdit(cc)} data-testid={`edit-cost-code-${cc.id}`} className="text-slate-400 hover:text-sky-600 inline-flex"><Pencil size={14} /></button>
                      <button onClick={() => toggleActive(cc)} data-testid={`toggle-cost-code-${cc.id}`} className="text-[12px] font-semibold text-sky-600">
                        {cc.active ? "Deactivate" : "Activate"}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {codes.length === 0 && (
              <tr><td colSpan={5} className="text-slate-400 text-sm text-center py-10">No cost codes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="cost-code-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Cost Code" : "New Cost Code"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code (e.g. 03-300)" data-testid="cost-code-code-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Concrete)" data-testid="cost-code-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (optional)" data-testid="cost-code-category-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <button onClick={save} data-testid="save-cost-code-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">{editing ? "Save Changes" : "Create Cost Code"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSelect} onOpenChange={setShowSelect}>
        <DialogContent data-testid="select-cost-codes-modal">
          <DialogHeader><DialogTitle>Select Accounts as Cost Codes</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500 -mt-1">Bank/loan/payroll accounts are unchecked by default — check any you still want as a cost code. Unchecking a selected account deactivates it here (history is kept).</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input value={selectSearch} onChange={(e) => setSelectSearch(e.target.value)} placeholder="Search accounts…" data-testid="select-cost-code-search" className="h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
            <SearchableSelect value={selectType} onChange={(e) => setSelectType(e.target.value)} data-testid="select-cost-code-type-filter" className="h-9 px-2 rounded-lg border border-slate-200 text-[13px]">
              <option value="">All account types</option>
              {[...new Set(selectable.map((r) => r.account_type).filter(Boolean))].sort().map((t) => <option key={t} value={t}>{t}</option>)}
            </SearchableSelect>
          </div>
          <div className="mt-2 max-h-80 overflow-y-auto ss-scrollbar space-y-1.5">
            {selectable.filter((r) => (!selectType || r.account_type === selectType) && (!selectSearch || r.name.toLowerCase().includes(selectSearch.toLowerCase()))).map((r) => (
              <label key={r.qbo_id} className="flex items-center gap-2.5 text-[13px] px-2.5 py-2 rounded-lg hover:bg-slate-50" data-testid={`select-cost-code-row-${r.qbo_id}`}>
                <input type="checkbox" checked={checkedIds.includes(r.qbo_id)} onChange={() => toggleChecked(r.qbo_id)} data-testid={`select-cost-code-checkbox-${r.qbo_id}`} />
                <span className="flex-1">{r.name}{r.is_sub_account && <span className="ml-1.5 text-[10.5px] text-slate-400">sub-account</span>}</span>
                <span className={`text-[10.5px] ${r.default_excluded || DEFAULT_EXCLUDED_TYPES.has(r.account_type) ? "text-slate-400" : "text-slate-500"}`}>{r.account_type}</span>
              </label>
            ))}
            {selectable.length === 0 && <div className="text-slate-400 text-[12.5px] text-center py-6">No chart of accounts synced yet — run Sync Now first.</div>}
          </div>
          <button disabled={savingSelection} onClick={saveSelection} data-testid="save-cost-code-selection-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-3 disabled:opacity-60">
            {savingSelection ? "Saving…" : "Save Selection"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
